import React, { useCallback, useState, useEffect } from 'react';
import { LoginForm } from './LoginForm';
import { SignupForm } from './SignupForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { EmailConfirmationPending } from './EmailConfirmationPending';
import { AnimatedBackground, Button } from '../ui';
import skylarqLogo from '../../assets/skylarq_logo_small.png';
import { EXTERNAL_LINKS } from '../../constants/links';

const signUpDisabled = false;

type AuthMode = 'hero' | 'login' | 'signup' | 'forgot-password' | 'pending-email-confirmation';

export const AuthPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [authMode, setAuthMode] = useState<AuthMode>('hero');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleModeChange = useCallback((newMode: AuthMode) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setAuthMode(newMode);
      setIsTransitioning(false);
    }, 150);
  }, []);

  const handleSignUpSuccess = useCallback((email: string) => {
    setEmail(email);
    handleModeChange('pending-email-confirmation');
  }, [handleModeChange]);

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center px-4 py-6 sm:p-6 relative overflow-hidden">
      <AnimatedBackground />

      <div
        className={`relative z-10 w-full transition-all duration-500 ease-out ${
          authMode === 'hero' ? 'max-w-[480px]' : 'max-w-[420px]'
        } ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        {authMode === 'hero' ? (
          <HeroView
            onGetStarted={() => handleModeChange('signup')}
            onSignIn={() => handleModeChange('login')}
            isTransitioning={isTransitioning}
          />
        ) : (
          <div
            className={`bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl shadow-primary-900/5 border border-neutral-200/80 transition-all duration-300 ${
              isTransitioning ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'
            }`}
          >
            {authMode === 'login' && (
              <LoginForm
                onToggleMode={() => !signUpDisabled && handleModeChange('signup')}
                onForgotPassword={() => handleModeChange('forgot-password')}
                onBack={() => handleModeChange('hero')}
              />
            )}
            {authMode === 'signup' && (
              <SignupForm
                onToggleMode={() => handleModeChange('login')}
                onSuccess={handleSignUpSuccess}
                onBack={() => handleModeChange('hero')}
              />
            )}
            {authMode === 'forgot-password' && (
              <ForgotPasswordForm onBack={() => handleModeChange('login')} />
            )}
            {authMode === 'pending-email-confirmation' && (
              <EmailConfirmationPending email={email} onBack={() => handleModeChange('login')} />
            )}
          </div>
        )}
      </div>

      <div
        className={`fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 sm:gap-4 z-10 transition-all duration-700 delay-300 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <a href={EXTERNAL_LINKS.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="text-[10px] sm:text-xs text-neutral-400 hover:text-neutral-600 transition-colors duration-200">
          Privacy
        </a>
        <span className="text-neutral-300">·</span>
        <a href={EXTERNAL_LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer" className="text-[10px] sm:text-xs text-neutral-400 hover:text-neutral-600 transition-colors duration-200">
          Terms
        </a>
        <span className="text-neutral-300">·</span>
        <span className="text-[10px] sm:text-xs text-neutral-400">© {new Date().getFullYear()} Skylarq</span>
      </div>
    </div>
  );
};

interface HeroViewProps {
  onGetStarted: () => void;
  onSignIn: () => void;
  isTransitioning?: boolean;
}

const HeroView: React.FC<HeroViewProps> = ({ onGetStarted, onSignIn, isTransitioning }) => {
  return (
    <div className={`text-center transition-all duration-300 px-2 ${isTransitioning ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'}`}>
      <div className="flex items-center justify-center gap-2 sm:gap-2.5 mb-6 sm:mb-8 animate-float">
        <div className="relative w-9 h-9 sm:w-10 sm:h-10 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md shadow-primary-900/5 border border-neutral-200/60 transition-all duration-500 hover:shadow-lg hover:bg-white/90">
          <img src={skylarqLogo} alt="Skylarq" className="w-6 h-6 sm:w-7 sm:h-7 object-contain opacity-90" />
        </div>
        <span className="text-[20px] sm:text-[22px] font-semibold text-neutral-700 tracking-tight">
          Skylarq
        </span>
      </div>

      <h1 className="text-[32px] sm:text-[38px] md:text-[44px] font-serif font-normal text-neutral-800 leading-[1.15] tracking-tight mb-3 sm:mb-4">
        Stop chasing.
        <br />
        <span className="text-neutral-400 transition-colors duration-300">Start closing.</span>
      </h1>

      <p className="text-sm sm:text-[15px] text-neutral-500 leading-relaxed mb-8 sm:mb-10 max-w-[300px] sm:max-w-[340px] mx-auto px-2">
        AI finds the decision-makers already looking for what you sell.
      </p>

      <div className="flex flex-col gap-2.5 sm:gap-3 max-w-[280px] sm:max-w-[300px] mx-auto">
        <Button
          onClick={onGetStarted}
          variant="primary"
          size="lg"
          fullWidth
          className="shadow-md shadow-primary-900/10 hover:shadow-lg hover:shadow-primary-900/15 sm:hover:-translate-y-0.5 sm:active:translate-y-0"
        >
          Start for free
        </Button>

        <Button
          onClick={onSignIn}
          variant="secondary"
          size="lg"
          fullWidth
        >
          I already have an account
        </Button>
      </div>

      <div className="mt-8 sm:mt-12">
        <div className="inline-flex items-center gap-3 sm:gap-5 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/80 backdrop-blur-md rounded-full border border-neutral-200/80 shadow-sm transition-all duration-300 hover:shadow-md hover:bg-white/90">
          <div className="text-center">
            <div className="text-sm sm:text-base font-semibold text-primary-900">50k+</div>
            <div className="text-[8px] sm:text-[9px] text-neutral-500 uppercase tracking-wider font-medium">Leads</div>
          </div>
          <div className="w-px h-5 sm:h-6 bg-neutral-200" />
          <div className="text-center">
            <div className="text-sm sm:text-base font-semibold text-primary-900">3x</div>
            <div className="text-[8px] sm:text-[9px] text-neutral-500 uppercase tracking-wider font-medium">Faster</div>
          </div>
          <div className="w-px h-5 sm:h-6 bg-neutral-200" />
          <div className="text-center">
            <div className="text-sm sm:text-base font-semibold text-primary-900">Free</div>
            <div className="text-[8px] sm:text-[9px] text-neutral-500 uppercase tracking-wider font-medium">To start</div>
          </div>
        </div>
      </div>
    </div>
  );
};
